import { Box, Button, Typography } from '@mui/material';
import {
  Band,
  GroupShell,
  LedgerHeader,
} from '../../../shared/components/ledger';
import { VARIATIONS_COLLAPSE, VARIATIONS_COLUMNS } from './columns';
import { VariationsLedgerRow } from './VariationsLedgerRow';
import type {
  VariationLedgerRowModel,
  VariationWeekGroup,
} from './toVariationRows';

interface VariationsLedgerProps {
  weeks: VariationWeekGroup[];
  /**
   * Only the weeks the user explicitly collapsed, so weeks arriving with the
   * next page open by default.
   */
  collapsedWeeks: ReadonlySet<string>;
  onToggleWeek: (key: string) => void;
  onOpenRow: (row: VariationLedgerRowModel) => void;
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

/** The feed carries no money, so the whole ledger renders values-off. */
export function VariationsLedger({
  weeks,
  collapsedWeeks,
  onToggleWeek,
  onOpenRow,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: VariationsLedgerProps) {
  return (
    <>
      <LedgerHeader
        columns={VARIATIONS_COLUMNS}
        collapse={VARIATIONS_COLLAPSE}
        titleLabel="Variation"
        showValues={false}
      />
      {weeks.map(week => (
        <GroupShell
          key={week.key}
          open={!collapsedWeeks.has(week.key)}
          onToggle={() => onToggleWeek(week.key)}
          header={(open, toggle) => (
            <Band
              collapse={VARIATIONS_COLLAPSE}
              primary={week.label}
              secondary={week.range}
              count={week.rows.length}
              open={open}
              onToggle={toggle}
              showValues={false}
              data-testid="variations-group-header"
            />
          )}
        >
          {week.rows.map(row => (
            <VariationsLedgerRow
              key={row.id}
              row={row}
              columns={VARIATIONS_COLUMNS}
              collapse={VARIATIONS_COLLAPSE}
              onOpen={onOpenRow}
            />
          ))}
        </GroupShell>
      ))}
      {hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {totalCount} variation{totalCount !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </>
  );
}
